import React from 'react';
import styled, { keyframes } from 'styled-components';
import { SuccessIcon, ErrorIcon, WarningIcon, InfoIcon, IconProps } from './Icons';

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

const SEVERITY_COLOR: Record<ToastSeverity, string> = {
  success: '#52c41a',
  error: '#ff4d4f',
  warning: '#faad14',
  info: '#1677ff',
};

// Drops in from above the viewport and settles — the notification slides down
// into place rather than fading, so it reads as arriving from outside the page.
const dropIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const dropOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-100%);
  }
`;

/* Fixed full-width strip so the toast centres against the VIEWPORT, not against
   whatever container it happens to be rendered in. pointer-events are disabled
   on the strip so it never swallows clicks on the page behind it. */
const ToastLayer = styled.div`
  position: fixed;
  top: 16px;
  left: 0;
  right: 0;
  z-index: 1500;
  display: flex;
  justify-content: center;
  pointer-events: none;
`;


const ToastCard = styled.div<{ $closing: boolean; $multiline: boolean }>`
  display: inline-flex;
  align-items: ${(p) => (p.$multiline ? 'flex-start' : 'center')};
  gap: 8px;
  max-width: ${(p) => (p.$multiline ? 'min(92vw, 560px)' : 'min(90vw, 480px)')};
  padding: ${(p) => (p.$multiline ? '12px 16px' : '9px 16px')};
  border-radius: 8px;
  background: #ffffff;
  /* No border: the elevation alone separates it from the page, which is what
     keeps it feeling light rather than like a bordered alert box. */
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);
  pointer-events: auto;
  animation: ${(p) => (p.$closing ? dropOut : dropIn)} 0.25s ease forwards;
`;

const ToastIcon = styled.span<{ $severity: ToastSeverity; $multiline: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => SEVERITY_COLOR[p.$severity]};
  line-height: 0;
  /* Nudge onto the first text line when the icon can no longer centre. */
  margin-top: ${(p) => (p.$multiline ? '2px' : '0')};
`;

const ToastMessage = styled.span`
  color: rgba(0, 0, 0, 0.88);
  font-size: 14px;
  line-height: 1.5715;
  word-break: break-word;
`;

const SEVERITY_ICON: Record<ToastSeverity, React.FC<IconProps>> = {
  success: SuccessIcon,
  error: ErrorIcon,
  warning: WarningIcon,
  info: InfoIcon,
};

interface ToastProps {
  open: boolean;
  /** A node, not just a string, so a report can list its items. */
  message: React.ReactNode;
  severity?: ToastSeverity;
  /** Auto-dismiss delay in ms. */
  duration?: number;
  onClose: () => void;
}

/**
 * Lightweight top-centre notification. Deliberately has no dismiss button and
 * no border — it auto-dismisses, so a close affordance would just add noise.
 */
export const Toast: React.FC<ToastProps> = ({
  open,
  message,
  severity = 'success',
  duration = 3000,
  onClose,
}) => {
  const [visible, setVisible] = React.useState(open);
  const [closing, setClosing] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setVisible(true);
      setClosing(false);
      return;
    }

    // Keep the node mounted for the exit animation before unmounting it.
    if (!visible) return;
    setClosing(true);
    const timer = window.setTimeout(() => setVisible(false), 250);
    return () => window.clearTimeout(timer);
  }, [open, visible]);

  React.useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(onClose, duration);
    return () => window.clearTimeout(timer);
  }, [open, duration, onClose]);

  if (!visible) return null;

  const Icon = SEVERITY_ICON[severity];
  const multiline = typeof message !== 'string';

  return (
    <ToastLayer>
      <ToastCard
        $closing={closing}
        $multiline={multiline}
        role="status"
        aria-live="polite"
      >
        <ToastIcon $severity={severity} $multiline={multiline}>
          <Icon />
        </ToastIcon>
        <ToastMessage>{message}</ToastMessage>
      </ToastCard>
    </ToastLayer>
  );
};

export default Toast;
